<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Badge } from '@drama/ui'
import { Film, Sparkles, FileText, Layers, ShieldCheck, Clock, Check, Copy, Code, RefreshCw } from '@lucide/vue'

const props = defineProps({
  record: { type: Object, required: true },
  screenplay: { type: Object, default: null },
  screenplayFromCurrentArtifact: { type: Boolean, default: false },
  providerPlan: { type: Object, default: null },
  planSegments: { type: Array, default: () => [] },
  sourceMaterial: { type: Object, default: null },
  opening: { type: Array, default: () => [] },
  turns: { type: Array, default: () => [] },
  references: { type: Array, default: () => [] },
  referenceUrls: { type: Object, default: () => ({}) },
  referenceStates: { type: Object, default: () => ({}) },
  linked: { type: Array, default: () => [] },
  bindingKey: { type: String, default: 'bindingUnavailable' },
  copiedKey: { type: String, default: '' },
})

const emit = defineEmits([
  'copyText',
  'loadReferences',
  'openSubmission',
])

const { t } = useI18n()

function safeValue(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return t('storyFilm.executionLog.notProvided')
  const text = String(value)
  if (!text) return t('storyFilm.executionLog.notProvided')
  const hidden = t('storyFilm.executionLog.sensitiveRecordHidden')
  return text
    .replace(/https?:\/\/[^\s"'<>]+|https?%3a%2f%2f[^\s"'<>]+|data:[^\s,]+;base64,[a-z0-9+/=]+|\b(?:bearer|basic)\s+\S+|\b(?:sk|ak)-[a-z0-9_-]{16,}|\beyJ[a-z0-9_-]{8,}\.eyJ[a-z0-9_-]{8,}\.[a-z0-9_-]{8,}/gi, hidden)
    .replace(/(\b(?:password|secret|api[_-]?key|access[_-]?token|refresh[_-]?token|signature)\s*[:=]\s*)(?:"[^"\r\n]*"|'[^'\r\n]*'|[^\s,;}]+)/gi, (_, field) => `${field}${hidden}`)
}

function sourceName(id) {
  if (id === 'narrator') return t('storyFilm.executionLog.narrator')
  if (id === 'player') return t('storyFilm.executionLog.player')
  const context = props.record.task?.public_context
  const entries = [...(Array.isArray(context?.characters) ? context.characters : []), ...(Array.isArray(context?.locations) ? context.locations : [])]
  const item = entries.find(entry => entry?.id === id)
  return safeValue(typeof item?.name === 'string' && item.name ? item.name : id)
}

function safeJson(value) {
  return JSON.stringify(value, (key, item) => {
    if (/base64/i.test(key) || /^(?:object_key|storage_key|url|path|image_data|reference_image_urls?)$/i.test(key) || /(?:^|[_-])(?:authorization|cookie|password|credentials?|secret|api[_-]?key|token|signature)(?:$|[_-])/i.test(key)) return t('storyFilm.executionLog.sensitiveRecordHidden')
    return typeof item === 'string' ? safeValue(item) : item
  }, 2)
}

function submissionId(value) {
  const id = Number(value)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

function submissionStatus(status) {
  const known = ['queued', 'producing', 'failed', 'review_ready', 'rejected', 'media_ready']
  return t(`storyFilm.executionLog.${known.includes(status) ? `status_${status}` : 'status_unknown'}`)
}

function parseH3Badges(h3) {
  if (!h3 || typeof h3 !== 'object') return []
  const badges = []
  if (h3.width && h3.height) badges.push({ key: 'res', label: '画面', value: `${h3.width}×${h3.height}` })
  if (h3.aspect_ratio) badges.push({ key: 'ratio', label: '画幅', value: h3.aspect_ratio })
  if (h3.frames) badges.push({ key: 'frames', label: '帧数', value: `${h3.frames}f` })
  if (h3.fps) badges.push({ key: 'fps', label: '帧率', value: `${h3.fps} fps` })
  if (h3.steps != null) badges.push({ key: 'steps', label: '步数', value: `${h3.steps}` })
  if (h3.seed != null) badges.push({ key: 'seed', label: '种子', value: `${h3.seed}` })
  if (h3.turbo) badges.push({ key: 'turbo', label: '加速', value: 'Turbo' })
  if (h3.model) badges.push({ key: 'model', label: '模型', value: String(h3.model) })
  return badges
}
</script>

<template>
  <div class="prep-artifacts-grid">
    <!-- 左主列：剧本分镜 + H3 制作计划 + 来源对话 -->
    <div class="dense-grid-col primary-col">
      <!-- 1. 剧本与分镜 -->
      <section class="dense-card" data-testid="preparation-screenplay">
        <div class="dense-card-head">
          <div class="head-left">
            <Film :size="14" class="card-glyph" />
            <h4>{{ t('storyFilmReview.screenplay') }}</h4>
          </div>
          <span v-if="screenplay" class="head-count-sub">
            {{ t(screenplayFromCurrentArtifact ? 'storyFilmReview.preparationScreenplayReturned' : 'storyFilmReview.verifiedScreenplay', { count: screenplay.scenes.length }) }}
          </span>
        </div>

        <template v-if="screenplay">
          <div class="dense-scenes-scroll">
            <article v-for="scene in screenplay.scenes" :key="scene.index" class="dense-scene-card">
              <div class="scene-head-strip">
                <span class="scene-no-tag">场景 {{ scene.index + 1 }}</span>
                <b class="scene-name">{{ safeValue(scene.title) }}</b>
              </div>
              <p v-if="scene.summary_en" class="scene-en-summary">{{ safeValue(scene.summary_en) }}</p>
              <div class="beats-compact-list">
                <div v-for="(beat, bIdx) in scene.beats ?? []" :key="bIdx" class="beat-compact-row">
                  <p class="beat-action-txt">{{ safeValue(beat.action) }}</p>
                  <div v-if="beat.dialogue && beat.dialogue.length" class="dialogue-compact-list">
                    <div v-for="(line, lIdx) in beat.dialogue" :key="lIdx" class="dense-dialogue-line">
                      <span class="speaker-label">{{ sourceName(line.speaker_id) }}:</span>
                      <span class="line-text">{{ safeValue(line.text) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </template>
        <p v-else class="dense-empty-line">{{ t('storyFilmReview.preparationScriptUnavailable') }}</p>
      </section>

      <!-- 2. H3 制作计划 (去 JSON 化，结构化逐镜紧凑卡片) -->
      <section class="dense-card" data-testid="preparation-plan">
        <div class="dense-card-head">
          <div class="head-left">
            <Sparkles :size="14" class="card-glyph" />
            <h4>{{ t('storyFilmReview.preparationPlan') }}</h4>
          </div>
          <span v-if="planSegments.length" class="head-count-sub">共 {{ planSegments.length }} 镜计划</span>
        </div>

        <template v-if="providerPlan && planSegments.length">
          <div class="dense-plan-scroll">
            <article v-for="segment in planSegments" :key="segment.index" class="dense-segment-card">
              <div class="segment-mini-head">
                <div class="seg-head-l">
                  <span class="seg-num">镜 {{ segment.index }}</span>
                  <span v-if="Number.isFinite(segment.duration_ms)" class="seg-dur">
                    {{ (segment.duration_ms / 1000).toFixed(2) }}s
                  </span>
                </div>
                <!-- H3 核心参数药丸 -->
                <div v-if="segment.request?.h3" class="seg-pills-row">
                  <span v-for="b in parseH3Badges(segment.request.h3)" :key="b.key" class="seg-mini-pill">
                    {{ b.label }}: <b>{{ b.value }}</b>
                  </span>
                </div>
              </div>

              <!-- 提示词卡片 (14px 宽大可读，支持复制) -->
              <div class="segment-prompt-row">
                <blockquote v-if="segment.request?.prompt" class="dense-prompt-box">
                  {{ safeValue(segment.request.prompt) }}
                </blockquote>
                <button
                  v-if="segment.request?.prompt"
                  type="button"
                  class="prompt-copy-action"
                  @click="emit('copyText', segment.request.prompt, `prompt-${segment.index}`)"
                >
                  <Check v-if="copiedKey === `prompt-${segment.index}`" :size="12" class="is-ok" />
                  <Copy v-else :size="12" />
                  <span>{{ copiedKey === `prompt-${segment.index}` ? '已复制' : '复制提示词' }}</span>
                </button>
              </div>

              <!-- 角色引用 -->
              <div v-if="Array.isArray(segment.request?.input?.references) && segment.request.input.references.length" class="seg-roles-strip">
                <span class="strip-label">素材参考:</span>
                <span v-for="refItem in segment.request.input.references" :key="refItem.ref" class="role-chip">
                  {{ safeValue(refItem.ref) }}
                </span>
              </div>

              <!-- 底层参数按需展开 -->
              <details v-if="segment.request?.h3" class="mini-raw-fold">
                <summary class="fold-sum"><Code :size="12" /><span>查看 H3 底层调用参数</span></summary>
                <pre class="dense-code-pre">{{ safeJson(segment.request.h3) }}</pre>
              </details>
            </article>
          </div>
        </template>
        <p v-else class="dense-empty-line">{{ t('storyFilmReview.preparationPlanUnavailable') }}</p>
      </section>

      <!-- 3. 来源正文 -->
      <section v-if="sourceMaterial" class="dense-card" data-testid="preparation-source-material">
        <div class="dense-card-head">
          <div class="head-left">
            <FileText :size="14" class="card-glyph" />
            <h4>{{ t('storyFilmReview.preparationSourceText') }}</h4>
          </div>
        </div>
        <div class="source-dialogue-scroll">
          <article v-for="(item, index) in opening" :key="`opening-${index}`" class="micro-bubble">
            <b class="bubble-speaker">{{ sourceName(item.speaker_id) }}:</b>
            <span class="bubble-txt">{{ safeValue(item.text) }}</span>
          </article>
          <section v-for="turn in turns" :key="turn.seq" class="turn-section">
            <span class="turn-anchor">{{ t('storyFilmReview.turn', { number: turn.seq }) }}</span>
            <article v-for="(item, index) in turn.segments ?? []" :key="index" class="micro-bubble">
              <b class="bubble-speaker">{{ sourceName(item.speaker_id) }}:</b>
              <span class="bubble-txt">{{ safeValue(item.text) }}</span>
            </article>
          </section>
        </div>
      </section>
    </div>

    <!-- 右侧列：素材图库 + 自评与批注 + 关联制作 -->
    <div class="dense-grid-col secondary-col">
      <!-- 1. 参考素材图库 -->
      <section v-if="references?.length" class="dense-card" data-testid="preparation-references">
        <div class="dense-card-head">
          <div class="head-left">
            <Layers :size="14" class="card-glyph" />
            <h4>{{ t('storyFilmReview.preparationMaterials') }}</h4>
          </div>
          <Button size="xs" variant="ghost" @click="emit('loadReferences')">
            <RefreshCw :size="12" />
          </Button>
        </div>
        <div class="dense-gallery-grid">
          <div v-for="(reference, index) in references" :key="`${reference.index}-${index}`" class="dense-thumb-box">
            <img v-if="referenceUrls[index]" :src="referenceUrls[index]" :alt="safeValue(reference.id)" class="dense-thumb-img" />
            <div v-else class="dense-thumb-placeholder">{{ t(referenceStates[index] ?? 'storyFilmReview.loading') }}</div>
            <span class="dense-thumb-caption">{{ sourceName(reference.id) }}</span>
          </div>
        </div>
      </section>

      <!-- 2. 生成物与叙事分析 -->
      <section class="dense-card" data-testid="preparation-artifacts">
        <div class="dense-card-head">
          <div class="head-left">
            <ShieldCheck :size="14" class="card-glyph" />
            <h4>{{ t('storyFilmReview.preparationArtifacts') }}</h4>
          </div>
        </div>

        <!-- 叙事分析 -->
        <div v-if="record.artifacts?.narrative_analysis" class="dense-artifact-unit">
          <div class="artifact-unit-head">
            <b>{{ t('storyFilmReview.preparationNarrativeAnalysis') }}</b>
          </div>
          <p class="artifact-unit-p">{{ safeValue(record.artifacts.narrative_analysis) }}</p>
        </div>

        <!-- 制作批注 -->
        <div v-if="record.artifacts?.production_notes" class="dense-artifact-unit">
          <div class="artifact-unit-head">
            <b>{{ t('storyFilmReview.preparationProductionNotes') }}</b>
          </div>
          <p class="artifact-unit-p">{{ safeValue(record.artifacts.production_notes) }}</p>
        </div>

        <!-- AI 自评报告 -->
        <div v-if="record.artifacts?.agent_self_report" class="dense-artifact-unit">
          <div class="artifact-unit-head">
            <b>{{ t('storyFilmReview.preparationSelfReport') }}</b>
          </div>
          <p v-if="typeof record.artifacts.agent_self_report === 'string'" class="artifact-unit-p">
            {{ safeValue(record.artifacts.agent_self_report) }}
          </p>
          <div v-else class="dense-kv-table">
            <div v-for="(v, k) in record.artifacts.agent_self_report" :key="k" class="dense-kv-row">
              <span class="dk-k">{{ k }}:</span>
              <span class="dk-v">{{ typeof v === 'object' ? safeJson(v) : safeValue(v) }}</span>
            </div>
          </div>
        </div>

        <!-- 编译结果 -->
        <div v-if="record.artifacts?.compile_result" class="dense-artifact-unit">
          <div class="artifact-unit-head">
            <b>{{ t('storyFilmReview.preparationCompileResult') }}</b>
            <Badge variant="success" size="sm">校验通过</Badge>
          </div>
          <details class="mini-raw-fold">
            <summary class="fold-sum"><Code :size="12" /><span>编译清单 Payload</span></summary>
            <pre class="dense-code-pre">{{ safeJson(record.artifacts.compile_result) }}</pre>
          </details>
        </div>
      </section>

      <!-- 3. 关联正式制作 -->
      <section class="dense-card" data-testid="preparation-binding">
        <div class="dense-card-head">
          <div class="head-left">
            <Clock :size="14" class="card-glyph" />
            <h4>{{ t('storyFilmReview.preparationBinding') }}</h4>
          </div>
          <Badge variant="neutral" size="sm">{{ t(`storyFilmReview.${bindingKey}`) }}</Badge>
        </div>
        <div v-if="linked.length" class="dense-linked-list">
          <div v-for="(item, index) in linked" :key="item.submission_id ?? index" class="dense-linked-row">
            <div class="dl-l">
              <b>受理 #{{ safeValue(item.submission_id) }}</b>
              <Badge :variant="item.status === 'media_ready' ? 'success' : 'info'" size="sm">{{ submissionStatus(item.status) }}</Badge>
            </div>
            <Button v-if="submissionId(item.submission_id)" size="sm" variant="secondary" @click="emit('openSubmission', submissionId(item.submission_id))">
              {{ t('storyFilm.executionLog.load') }}
            </Button>
          </div>
        </div>
        <p v-else class="dense-empty-line">暂无关联的正式制作任务</p>
      </section>
    </div>
  </div>
</template>

<style scoped>
.prep-artifacts-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
  width: 100%;
}

@media (max-width: 1080px) {
  .prep-artifacts-grid {
    grid-template-columns: 1fr;
  }
}

.dense-grid-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.dense-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 12px 14px;
}

.dense-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--drama-color-border);
}

.head-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dense-card-head h4 {
  margin: 0;
  font-size: var(--drama-font-size-sm, 14px);
  font-weight: 600;
}

.card-glyph {
  color: var(--drama-color-primary);
}

.head-count-sub {
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
}

.dense-empty-line {
  margin: 6px 0;
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
  font-style: italic;
}

.dense-scenes-scroll {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
}

.dense-scene-card {
  padding: 10px 12px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.scene-head-strip {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scene-no-tag {
  font-size: var(--drama-font-size-xs, 12px);
  font-weight: 700;
  padding: 2px 8px;
  background: var(--drama-color-primary);
  color: #fff;
  border-radius: var(--drama-radius-xs, 4px);
}

.scene-name {
  font-size: var(--drama-font-size-sm, 14px);
}

.scene-en-summary {
  margin: 0;
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
  font-style: italic;
  line-height: 1.4;
}

.beats-compact-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.beat-action-txt {
  margin: 0;
  font-size: var(--drama-font-size-sm, 14px);
  color: var(--drama-color-text);
  line-height: 1.5;
}

.dialogue-compact-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
  border-left: 3px solid var(--drama-color-primary);
  margin-top: 4px;
}

.dense-dialogue-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: var(--drama-font-size-sm, 14px);
  line-height: 1.5;
}

.speaker-label {
  font-weight: 600;
  color: var(--drama-color-primary);
  flex-shrink: 0;
}

.dense-plan-scroll {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.dense-segment-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
}

.segment-mini-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
}

.seg-head-l {
  display: flex;
  align-items: center;
  gap: 6px;
}

.seg-num {
  font-size: var(--drama-font-size-xs, 12px);
  font-weight: 700;
  padding: 3px 8px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-xs, 4px);
}

.seg-dur {
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-info);
}

.seg-pills-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.seg-mini-pill {
  font-size: var(--drama-font-size-xs, 12px);
  padding: 3px 8px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-xs, 4px);
  color: var(--drama-color-text-muted);
}

.seg-mini-pill b {
  color: var(--drama-color-text);
}

.segment-prompt-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.dense-prompt-box {
  margin: 0;
  flex: 1;
  padding: 8px 10px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-left: 3px solid var(--drama-color-primary);
  border-radius: var(--drama-radius-xs, 3px);
  font-size: var(--drama-font-size-sm, 14px);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: var(--drama-font-family-mono);
}

.prompt-copy-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: var(--drama-font-size-xs, 12px);
  border: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
  border-radius: var(--drama-radius-xs, 3px);
  color: var(--drama-color-text-muted);
  cursor: pointer;
  flex-shrink: 0;
}

.prompt-copy-action:hover {
  background: var(--drama-color-surface-alt);
  color: var(--drama-color-text);
}

.seg-roles-strip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--drama-font-size-xs, 12px);
}

.strip-label {
  color: var(--drama-color-text-muted);
}

.role-chip {
  padding: 3px 8px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-xs, 4px);
  font-size: var(--drama-font-size-xs, 12px);
}

.mini-raw-fold {
  font-size: var(--drama-font-size-xs, 12px);
  border-top: 1px dashed var(--drama-color-border);
  padding-top: 6px;
}

.fold-sum {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: var(--drama-color-text-muted);
}

.dense-code-pre {
  margin: 6px 0 0;
  padding: 8px;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs, 12px);
  max-height: 180px;
  overflow: auto;
  white-space: pre-wrap;
}

.source-dialogue-scroll {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
}

.micro-bubble {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 10px;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
  font-size: var(--drama-font-size-sm, 14px);
}

.bubble-speaker {
  font-weight: 600;
  color: var(--drama-color-primary);
  flex-shrink: 0;
}

.bubble-txt {
  line-height: 1.5;
}

.turn-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.turn-anchor {
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
  font-weight: 600;
  margin-top: 4px;
}

.dense-gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(88px, 1fr));
  gap: 8px;
}

.dense-thumb-box {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 4px;
  background: var(--drama-color-surface-alt);
}

.dense-thumb-img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: var(--drama-radius-xs, 3px);
}

.dense-thumb-placeholder {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--drama-font-size-xs, 12px);
  color: var(--drama-color-text-muted);
}

.dense-thumb-caption {
  font-size: var(--drama-font-size-xs, 12px);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dense-artifact-unit {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
}

.artifact-unit-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--drama-font-size-sm, 14px);
}

.artifact-unit-p {
  margin: 0;
  font-size: var(--drama-font-size-sm, 14px);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.dense-kv-table {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.dense-kv-row {
  display: flex;
  gap: 6px;
  font-size: var(--drama-font-size-xs, 12px);
}

.dk-k {
  color: var(--drama-color-text-muted);
  font-weight: 600;
}

.dense-linked-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dense-linked-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  font-size: var(--drama-font-size-sm, 14px);
}

.dl-l {
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>

